import React from "react";
import { useLocation } from "react-router-dom";
import { report } from "../utils/errorReporting";
import "./routeErrorBoundary.css";

/*
 * Catches a route that fails to load, which since route-level code splitting
 * has a specific and common cause: a deploy.
 *
 * The app loads each of its 50 routes as its own content-hashed chunk. A tab
 * that was open when a deploy landed still holds the old chunk map, so the
 * first navigation to a route whose chunk was rehashed requests a file that no
 * longer exists. With nothing catching that, React unmounted the whole content
 * subtree: the page went blank, the tab title still named the page as though it
 * had worked, and nothing on screen suggested a reload. Only the header
 * survived, because it sits outside this boundary.
 *
 * It waits to be asked rather than reloading itself. An automatic reload reads
 * as a glitch -- a prompt appears for an instant, the page flickers, and the
 * visitor is left wondering what happened -- and it takes the decision away at
 * the one moment the page is already behaving oddly. The reload keeps the
 * current URL, so clicking it lands back on the same page, now on the new
 * version. Same reasoning as VersionUpdatePill, which never reloads either.
 */

/*
 * A stale bundle surfaces under several messages depending on the browser and
 * on whether the missing file 404s or gets answered with index.html by the SPA
 * rewrite -- in which case the parser trips over the doctype instead.
 */
function isStaleBundle(error) {
  const text = `${error?.name || ""} ${error?.message || ""}`;

  return /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module|Importing a module script failed|Unexpected token '<'|expected expression, got '<'/i.test(
    text
  );
}

class Boundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, stale: false };
  }

  static getDerivedStateFromError(error) {
    return { failed: true, stale: isStaleBundle(error) };
  }

  componentDidCatch(error, info) {
    /*
     * The one place an exception is already known about, and it used to end
     * here. A stale bundle is expected -- a deploy causes it, and the notice
     * below handles it -- so only real failures are worth reporting.
     */
    if (isStaleBundle(error)) return;

    report(error, {
      route: window.location.pathname,
      componentStack: info?.componentStack,
    });
  }

  render() {
    const { failed, stale } = this.state;

    if (!failed) return this.props.children;

    return (
      <div className="ctb-routeError" role="alert">
        <h1 className="ctb-routeError-title">
          {stale ? "This page was updated" : "This page could not be loaded"}
        </h1>

        <p className="ctb-routeError-copy">
          {stale
            ? "A new version of the site went live while this tab was open. Reload to pick it up and carry on from here."
            : "Something went wrong while opening this page. Reloading usually clears it."}
        </p>

        <div className="ctb-routeError-actions">
          <button
            type="button"
            className="ctb-routeError-btn"
            onClick={() => window.location.reload()}
          >
            Reload the page
          </button>

          {/*
            A plain anchor, not a react-router Link: React.lazy caches a
            rejected import for the life of the page, so a client-side hop can
            land straight back in the broken route. Only a fresh document
            reliably recovers.
          */}
          <a className="ctb-routeError-link" href="/">
            Go to the home page
          </a>
        </div>
      </div>
    );
  }
}

/*
 * Keyed on the path so a new route gets a fresh boundary rather than inheriting
 * the previous one's error state. React remounts the subtree on a route change
 * anyway, so the key costs nothing.
 */
export default function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();

  return <Boundary key={pathname}>{children}</Boundary>;
}
